// ============================================================
// MODERN LAYOUT - Wrapper component for sidebar + topbar layout
// File: src/components/ModernLayout.js
// ============================================================

import React, { useState } from 'react'
import ModernSidebar from './ModernSidebar'
import { useTheme } from '../ThemeContext'
import { useLayout } from '../LayoutContext'
import {
  LayoutDashboard, Building2, ClipboardList, PhoneCall, CalendarDays,
  Calendar, Shield, AlertCircle, UserX, UserCheck, Scale, NotebookPen,
  ShieldAlert, Menu, X, Sun, Moon, BarChart2, Gauge, GraduationCap, HelpCircle, Receipt, BookUser, ScanSearch
} from 'lucide-react'

export default function ModernLayout({
  children,
  currentView,
  onNavigate,
  user,
  onSignOut,
  organization,
  isSuperAdmin,
  onSwitchOrganization,
  userRole
}) {
  const { darkMode, toggleDarkMode } = useTheme()
  useLayout()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const bgGradient = darkMode
    ? 'linear-gradient(155deg, #0e1825, #111e2e, #142030)'
    : 'linear-gradient(155deg, #d8e3ef, #e4ecf5, #dce7f2)'
  const orb1 = darkMode
    ? 'radial-gradient(circle, rgba(30,70,120,0.35), transparent 70%)'
    : 'radial-gradient(circle, rgba(61,100,148,0.22), transparent 70%)'
  const orb2 = darkMode
    ? 'radial-gradient(circle, rgba(20,60,110,0.28), transparent 70%)'
    : 'radial-gradient(circle, rgba(90,127,173,0.16), transparent 70%)'
  const orb3 = darkMode
    ? 'radial-gradient(circle, rgba(14,50,100,0.22), transparent 70%)'
    : 'radial-gradient(circle, rgba(42,80,130,0.14), transparent 70%)'

  const isStoreRole = userRole === 'store'
  const showSignalThree = isSuperAdmin || user?.email === 'josh@signalthree.com'

  const handleNavigate = (view) => {
    setMobileMenuOpen(false)
    onNavigate(view)
  }

  // Nav items for mobile menu — must stay in sync with ModernSidebar navItems
  const mobileNavItems = [
    { id: 'dashboard',      label: 'Dashboard',        icon: BarChart2,       show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'main',           label: 'Inspections',      icon: LayoutDashboard, show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'new-opening',    label: 'New Opening',      icon: Building2,       show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'daily-log',      label: 'Daily Log',        icon: ClipboardList,   show: !isStoreRole && organization?.features?.daily_activity_log },
    { id: 'on-call',        label: 'On-Call',          icon: PhoneCall,       show: !isStoreRole && organization?.features?.on_call_management },
    { id: 'monthly',        label: 'Monthly',          icon: CalendarDays,    show: !isStoreRole && organization?.features?.monthly_reports },
    { id: 'scheduling',     label: 'Scheduling',       icon: Calendar,        show: !isStoreRole && organization?.features?.scheduling },
    { id: 'complaints',     label: 'Complaints',       icon: AlertCircle,     show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'trespass',       label: 'Trespass',         icon: UserX,           show: organization?.features?.trespass_list },
    { id: 'minor-campaign', label: 'Minor Campaign',   icon: UserCheck,       show: !isStoreRole && organization?.features?.minor_campaign },
    { id: 'larceny',        label: 'Larceny',          icon: ShieldAlert,     show: !isStoreRole && organization?.features?.larceny_reports },
    { id: 'payments',      label: 'Payments',         icon: Receipt,         show: !isStoreRole && organization?.features?.payment_receipts },
    { id: 'rasp',           label: 'RASP Class',       icon: GraduationCap,   show: !isStoreRole },
    { id: 'abc-law',        label: 'ABC AI',           icon: Scale,           show: !isStoreRole && !!organization?.features?.abc_law },
    { id: 'notes',          label: 'My Notes',         icon: NotebookPen,     show: !isStoreRole },
    { id: 'contacts',       label: 'Contact Info',     icon: BookUser,        show: !isStoreRole && !!organization?.features?.contact_directory },
    { id: 'till-detective', label: 'Till Detective',   icon: ScanSearch,      show: !isStoreRole && !!organization?.features?.till_detective && (userRole === 'admin' || isSuperAdmin) },
    { id: 'admin',          label: 'Admin',            icon: Shield,          show: !isStoreRole && (userRole === 'admin' || isSuperAdmin) },
    { id: 'help',           label: 'Help',             icon: HelpCircle,      show: !isStoreRole },
    { id: 'super-admin',   label: 'Signal Three',     icon: Gauge,           show: showSignalThree },
  ].filter(item => item.show)

  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', position: 'relative' }}>

      {/* Fixed gradient background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: bgGradient, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: '600px', height: '600px', borderRadius: '50%', background: orb1, filter: 'blur(80px)', opacity: 0.15, top: '-120px', left: '-80px', animation: 'orbDrift1 22s ease-in-out infinite alternate' }} />
        <div style={{ position: 'absolute', width: '500px', height: '500px', borderRadius: '50%', background: orb2, filter: 'blur(80px)', opacity: 0.15, bottom: '-60px', right: '10%', animation: 'orbDrift2 18s ease-in-out infinite alternate' }} />
        <div style={{ position: 'absolute', width: '360px', height: '360px', borderRadius: '50%', background: orb3, filter: 'blur(80px)', opacity: 0.15, top: '40%', right: '30%', animation: 'orbDrift3 26s ease-in-out infinite alternate' }} />
      </div>

      <style>{`
        @keyframes orbDrift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(30px,20px) scale(1.06); } }
        @keyframes orbDrift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(-25px,15px) scale(1.04); } }
        @keyframes orbDrift3 { from { transform: translate(0,0) scale(1); } to { transform: translate(20px,-20px) scale(1.08); } }

        .modern-sidebar-wrapper { display: flex; }
        .modern-mobile-header   { display: none; }
        .modern-statusbar-fill  { display: none; }

        @media (max-width: 768px) {
          .modern-sidebar-wrapper { display: none !important; }
          .modern-mobile-header   { display: flex !important; }
          .modern-statusbar-fill  { display: block !important; }
          .modern-topbar-wrapper  { display: none !important; }
        }
      `}</style>

      {/* Phone status-bar strip (safe area above the header): navy to match the header,
          so the phone's clock/battery sit on a dark bar instead of the light page background */}
      <div className="modern-statusbar-fill" style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: 'env(safe-area-inset-top, 0px)',
        background: '#080f1e', zIndex: 250, pointerEvents: 'none',
      }} />

      {/* Desktop sidebar */}
      <div className="modern-sidebar-wrapper" style={{ position: 'relative', zIndex: 2, flexShrink: 0 }}>
        <ModernSidebar
          currentView={currentView}
          onNavigate={onNavigate}
          userRole={userRole}
          isSuperAdmin={isSuperAdmin}
          organization={organization}
          user={user}
          onSignOut={onSignOut}
          onSwitchOrganization={onSwitchOrganization}
        />
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 3, minWidth: 0 }}>

        {/* Mobile Header (hamburger bar) */}
        <div className="modern-mobile-header" style={{
          background: '#0f1f3d',   // one even navy (was a dark-to-light diagonal gradient)
          color: 'white',
          padding: '0 1rem',
          height: '54px',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          position: 'relative',
          zIndex: 200,
        }}>
          {/* The logo artwork is dark navy and grey for light backgrounds; on the navy header it is drawn in white */}
          <img src="/abcaxis-logo.png" alt="ABCAxis" style={{ height: '28px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={toggleDarkMode}
              style={{ padding: '6px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              style={{ padding: '6px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Mobile dropdown menu */}
          {mobileMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '54px',
              left: 0,
              right: 0,
              background: darkMode ? '#0a1220' : '#f8fbff',
              borderBottom: `1px solid ${darkMode ? 'rgba(90,127,173,0.15)' : 'rgba(61,100,148,0.12)'}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              zIndex: 300,
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              // The full menu is taller than a phone screen: scroll inside it so the last items
              // and Sign Out stay reachable (below the 54px header and the bottom safe area).
              maxHeight: 'calc(100dvh - 54px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}>
              {mobileNavItems.map(item => {
                const Icon = item.icon
                const active = currentView === item.id
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 14px',
                      background: active ? (darkMode ? 'rgba(90,127,173,0.18)' : 'rgba(61,100,148,0.1)') : 'transparent',
                      border: 'none', borderRadius: '8px', cursor: 'pointer',
                      color: active ? (darkMode ? '#c8daf5' : '#1e3a5f') : (darkMode ? 'rgba(200,218,245,0.7)' : '#4a6080'),
                      fontSize: '15px', fontWeight: active ? '600' : '400',
                      fontFamily: "'Source Sans 3', system-ui, sans-serif",
                      textAlign: 'left', width: '100%',
                    }}
                  >
                    <Icon size={18} style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />
                    {item.label}
                  </button>
                )
              })}

              {/* Sign out at bottom of mobile menu */}
              <div style={{ borderTop: `1px solid ${darkMode ? 'rgba(90,127,173,0.15)' : 'rgba(61,100,148,0.1)'}`, marginTop: '4px', paddingTop: '4px' }}>
                <button
                  onClick={() => { setMobileMenuOpen(false); onSignOut() }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', width: '100%',
                    background: 'transparent', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    color: darkMode ? 'rgba(200,218,245,0.6)' : '#7a90a8',
                    fontSize: '15px', fontFamily: "'Source Sans 3', system-ui, sans-serif",
                    textAlign: 'left',
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Top Bar — hidden; controls moved to sidebar footer */}
        {/* <div className="modern-topbar-wrapper" style={{ flexShrink: 0 }}>
          <ModernTopBar ... />
        </div> */}

        {/* Demo Banner */}
        {organization?.slug === 'demo' && (
          <div style={{ background: '#b91c1c', color: '#ffffff', textAlign: 'center', padding: '6px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.08em', flexShrink: 0 }}>
            ⚠ DEMO ORGANIZATION
          </div>
        )}

        {/* Main Content */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: 'transparent', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </main>
      </div>
    </div>
  )
}
